import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  TextField,
  InputAdornment,
} from "@mui/material";
import { ExpandMore, AutoAwesome } from "@mui/icons-material";
import { useAccelContext } from "../../contexts/AccelContext/AccelContext";
import { Message } from "../../types/Message";
import { useMediaQuery, useTheme } from "@mui/material";
import useSWR from "swr";
import {
  getChats,
  getChatMessages,
  sendMessage,
  createChat,
} from "../../api/accel/chat";

const AiReportsComponent = () => {
  const { theme: accelTheme, apiUrl, auth, companyId } = useAccelContext();
  const muiTheme = useTheme();

  const [inputValue, setInputValue] = useState("");
  const [selectedChat, setSelectedChat] = useState<string>("");

  const {
    data: chats,
    error: chatsError,
    mutate: mutateChats,
  } = useSWR(`chats-${companyId}`, 
    getChats(apiUrl, auth.access_token, { params: { companyId } })
  );

  const {
    data: messages,
    error: messagesError,
    mutate: mutateMessages,
  } = useSWR(selectedChat ? `messages-${selectedChat}` : null,
    getChatMessages(apiUrl, auth.access_token, {
      params: { companyId, chatId: selectedChat },
    })
  );

  useEffect(() => {
    if (chats) {
 
    }
  }, [chats]);

  useEffect(() => {
    if (selectedChat) {
      mutateMessages();
    }
  }, [selectedChat, mutateMessages]);

  const handleSendMessage = async () => {
    if (inputValue.trim()) {
      if (selectedChat) {
        await sendMessage(apiUrl, auth.access_token, {
          params: {
            companyId,
            chatId: selectedChat,
            prompt: inputValue.trim(),
          },
        });
      } else {
        const newChat = await createChat(apiUrl, auth.access_token, {
          params: { companyId, prompt: inputValue.trim() },
        });
        setSelectedChat(newChat.id.toString());
      }
      setInputValue("");
      mutateMessages();
      mutateChats();
    }
  };

  const handleDeleteChat = async (chatId: string) => {};

  const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down("md"));

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      width="100%"
      overflow="auto"
      bgcolor={accelTheme?.accent?.hex || "#1E1E1E"}
    >
      <Box
        display="flex"
        flexDirection={isSmallScreen ? "column" : "row"}
        flexGrow={1}
        p={3}
        gap={3}
      >
        {/* Sidebar/Top bar */}
        <Box
          width={isSmallScreen ? "100%" : "25%"}
          display="flex"
          flexDirection={isSmallScreen ? "row" : "column"}
          bgcolor={accelTheme?.accent?.hex || "#2C2C2C"}
          borderRadius="8px"
          mb={isSmallScreen ? 2 : 0}
        >
          <Button
            variant="outlined"
            onClick={() => setSelectedChat("")}
            sx={{
              m: 2,
              color: accelTheme?.primary?.hex || "#8E05C2",
              borderColor: accelTheme?.primary?.hex || "#8E05C2",
              borderRadius: "20px",
              flexShrink: 0,
              "&:hover": {
                borderColor: accelTheme?.primary?.hex || "#8E05C2",
                backgroundColor: "rgba(142, 5, 194, 0.1)",
              },
            }}
          >
            New Chat +
          </Button>

          <Box
            flexGrow={1}
            display="flex"
            flexDirection={isSmallScreen ? "row" : "column"}
          >
            <Accordion
              defaultExpanded={!isSmallScreen}
              sx={{
                backgroundColor: "transparent",
                boxShadow: "none",
                "&:before": { display: "none" },
                flexGrow: 1,
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMore
                    sx={{ color: accelTheme?.text?.hex || "#FFFFFF" }}
                  />
                }
              >
                <Typography sx={{ color: accelTheme?.text?.hex || "#FFFFFF" }}>
                  Chat History
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <List>
                  {chats && chats.length > 0 ? (
                    chats.map((chat) => (
                      <ListItem
                        key={chat.id}
                        button
                        onClick={() => setSelectedChat(chat.id.toString())}
                        selected={selectedChat === chat.id.toString()}
                      >
                        <ListItemText
                          primary={chat.name || `Chat ${chat.id}`}
                          sx={{ color: accelTheme?.text?.hex || "#FFFFFF" }}
                        />
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteChat(chat.id.toString());
                          }}
                          sx={{ color: accelTheme?.text?.hex || "#FFFFFF" }}
                        >
                          Delete
                        </Button>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary="No chats available"
                        sx={{ color: accelTheme?.text?.hex || "#FFFFFF" }}
                      />
                    </ListItem>
                  )}
                </List>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>

        {/* Divider */}
        {!isSmallScreen && (
          <Box
            sx={{
              width: "1px",
              bgcolor: accelTheme?.primary?.hex || "#8E05C2",
              opacity: 0.5,
            }}
          />
        )}

        {/* Main chat area */}
        <Box
          flexGrow={1}
          display="flex"
          flexDirection="column"
          bgcolor={accelTheme?.accent?.hex || "#2C2C2C"}
          borderRadius="8px"
        >
          <Box flexGrow={1} p={2} overflow="auto">
            {messages &&
              messages.map((message: Message, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent:
                      message.role === "USER" ? "flex-end" : "flex-start",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor:
                        message.role === "USER"
                          ? accelTheme?.primary?.hex || "#8E05C2"
                          : accelTheme?.secondary?.hex || "#3C3C3C",
                      color: accelTheme?.text?.hex || "#FFFFFF",
                      p: 2,
                      borderRadius: "8px",
                      maxWidth: "70%",
                    }}
                  >
                    {message.role !== "USER" && (
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor:
                            accelTheme?.primary?.hex || "#8E05C2",
                          mr: 1,
                        }}
                      />
                    )}
                    <Typography variant="body1">{message.content}</Typography>
                  </Box>
                </Box>
              ))}
          </Box>

          <Box display="flex" p={2} gap={2} alignItems="center">
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Talk to your finances"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <AutoAwesome
                      sx={{
                        color: accelTheme?.primary?.hex || "#8E05C2",
                        fontSize: "1rem",
                      }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  color: accelTheme?.text?.hex || "#FFFFFF",
                  backgroundColor: accelTheme?.accent?.hex || "#1E1E1E",
                  borderRadius: "20px",
                  fontSize: "0.875rem",
                  "& fieldset": {
                    borderColor: accelTheme?.primary?.hex || "#8E05C2",
                  },
                  "&:hover fieldset": {
                    borderColor: accelTheme?.primary?.hex || "#8E05C2",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: accelTheme?.primary?.hex || "#8E05C2",
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSendMessage}
              sx={{
                bgcolor: accelTheme?.primary?.hex || "#8E05C2",
                color: accelTheme?.accent?.hex || "#FFFFFF",
                borderRadius: "20px",
                whiteSpace: "nowrap",
                fontSize: "0.75rem",
                padding: "6px 12px",
                "&:hover": {
                  bgcolor: accelTheme?.primary?.hex || "#8E05C2",
                },
              }}
            >
              Chat
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AiReportsComponent;
